import { create } from 'zustand';
import { employeesApi } from '../services/api';
import { ORGANIZATIONAL_STATUSES } from '../constants/organizationalStatuses';
import { EMPLOYEE_REPORTING_SCOPE } from '../constants/employeeReportingScope';

// 1. Central initial state for easy reset
const initialState = {
  employees: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  searchTerm: '',
  orderBy: 'fullName',
  orderDirection: 'asc',
  limit: 30,
  // Filters
  positionIds: '',
  gradeIds: '',
  projectIds: '',
  organizationalStatus: [ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING].join(','),
  clusterId: '',
  reportingScope: EMPLOYEE_REPORTING_SCOPE.ALL,
  // UI State
  scrollPosition: 0,
  lastCardId: null,
  isReturningFromDetails: false,
  uiSearchInput: '',
  selectedPositions: [],
  selectedGrades: [],
  selectedProjects: [],
  selectedStatuses: [ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING],
  selectedCluster: '',
};

const useEmployeesStoreRepository = create((set, get) => {
  // 2. Helper: param construction
  const buildParams = (overrides = {}) => {
    const state = get();
    return {
      limit: state.limit,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.pageInfo.endCursor,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      search: overrides.search !== undefined ? overrides.search : state.searchTerm,
      positionIds: overrides.positionIds !== undefined ? overrides.positionIds : state.positionIds,
      gradeIds: overrides.gradeIds !== undefined ? overrides.gradeIds : state.gradeIds,
      projectIds: overrides.projectIds !== undefined ? overrides.projectIds : state.projectIds,
      organizationalStatus: overrides.organizationalStatus !== undefined ? overrides.organizationalStatus : state.organizationalStatus,
      clusterId: overrides.clusterId !== undefined ? overrides.clusterId : state.clusterId,
      reportingScope: overrides.reportingScope !== undefined ? overrides.reportingScope : state.reportingScope,
      ...overrides,
    };
  };

  // 3. Generic fetcher (reset/append)
  const fetchEmployees = async ({ reset = false, overrides = {} } = {}) => {
    
    if (get().isLoading || get().isLoadingMore) {
      return null;
    }

    if (reset) set({ isLoading: true, error: null });
    else set({ isLoadingMore: true, error: null });

    const params = buildParams(reset ? { ...overrides, cursor: null } : overrides);
    
    try {
      // Transform params to match API expectations
      const apiParams = {
        limit: params.limit,
        cursor: params.cursor,
        orderBy: params.orderBy,
        orderDirection: params.orderDirection,
        search: params.search,
        positionIds: params.positionIds || '',
        gradeIds: params.gradeIds || '',
        projectIds: params.projectIds || '',
        organizationalStatus: params.organizationalStatus || '',
        clusterId: params.clusterId || '',
        reportingScope: params.reportingScope || '',
      };

      const response = await employeesApi.getAllEmployees(apiParams);

      if (response) {
        const newEmployees = reset ? response.items : [...get().employees, ...response.items];
        const pageInfo = {
          endCursor: response.pageInfo?.endCursor || null,
          hasNextPage: response.pageInfo?.hasNextPage || false,
          totalCount: response.pageInfo?.totalCount || response.items.length,
        };
        
        set({
          employees: newEmployees,
          pageInfo,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false, isLoadingMore: false });
      return null;
    } catch (err) {
      set({ 
        error: err.message || 'Failed to load employees',
        isLoading: false,
        isLoadingMore: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // UI State Actions
    setScrollPosition: (position) => set({ scrollPosition: position }),
    setLastCardId: (id) => set({ lastCardId: id }),
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setUISearchInput: (input) => set({ uiSearchInput: input }),
    
    // Filter setters (simple state setters only, like CV Repository)
    setSelectedPositions: (positions) => set({ selectedPositions: positions }),
    setSelectedGrades: (grades) => set({ selectedGrades: grades }),
    setSelectedProjects: (projects) => set({ selectedProjects: projects }),
    setSelectedStatuses: (statuses) => set({ selectedStatuses: statuses }),
    setSelectedCluster: (cluster) => set({ selectedCluster: cluster }),

    // Filters
    setSearchTerm: (searchTerm, skipRefetch = false) => {
      set({ searchTerm });
      if (!skipRefetch) get().fetchEmployeesWithReset({ search: searchTerm });
    },
    setOrderBy: (orderBy, skipRefetch = false) => {
      set({ orderBy });
      if (!skipRefetch) get().fetchEmployeesWithReset({ orderBy });
    },
    setOrderDirection: (orderDirection, skipRefetch = false) => {
      set({ orderDirection });
      if (!skipRefetch) get().fetchEmployeesWithReset({ orderDirection });
    },
    setLimit: (limit, skipRefetch = false) => {
      set({ limit });
      if (!skipRefetch) get().fetchEmployeesWithReset({ limit });
    },
    setPositionIds: (positionIds, skipRefetch = false) => {
      set({ positionIds });
      if (!skipRefetch) get().fetchEmployeesWithReset({ positionIds });
    },
    setGradeIds: (gradeIds, skipRefetch = false) => {
      set({ gradeIds });
      if (!skipRefetch) get().fetchEmployeesWithReset({ gradeIds });
    },
    setProjectIds: (projectIds, skipRefetch = false) => {
      set({ projectIds });
      if (!skipRefetch) get().fetchEmployeesWithReset({ projectIds });
    },
    setOrganizationalStatus: (organizationalStatus, skipRefetch = false) => {
      set({ organizationalStatus });
      if (!skipRefetch) get().fetchEmployeesWithReset({ organizationalStatus });
    },
    setClusterId: (clusterId, skipRefetch = false) => {
      set({ clusterId });
      if (!skipRefetch) get().fetchEmployeesWithReset({ clusterId });
    },
    setReportingScope: (reportingScope, skipRefetch = false) => {
      set({ reportingScope });
      if (!skipRefetch) get().fetchEmployeesWithReset({ reportingScope });
    },

    // 4. Data actions
    fetchEmployeesWithReset: (overrides = {}) => fetchEmployees({ reset: true, overrides }),
    fetchEmployeesLoadMore: () => fetchEmployees({ reset: false }),

    updateEmployee: (updatedEmployee) => {
      if (!updatedEmployee || !updatedEmployee.id) {
        return false;
      }
      const { employees, pageInfo } = get();
      const index = employees.findIndex(emp => emp.id === updatedEmployee.id);
      
      if (index !== -1) {
        // Update existing employee
        const updatedEmployees = [...employees];
        updatedEmployees[index] = updatedEmployee;
        set({ employees: updatedEmployees });
        return true;
      } else {
        // Add new employee and increment total count
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: pageInfo.totalCount + 1
        };
        set({ 
          employees: [updatedEmployee, ...employees],
          pageInfo: updatedPageInfo
        });
        return true;
      }
    },

    // Filters reset
    resetFilters: () => {
      set({
        searchTerm: '',
        uiSearchInput: '',
        selectedPositions: [],
        selectedGrades: [],
        selectedProjects: [],
        selectedStatuses: [ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING],
        selectedCluster: '',
        positionIds: '',
        gradeIds: '',
        projectIds: '',
        organizationalStatus: [ORGANIZATIONAL_STATUSES.EMPLOYED, ORGANIZATIONAL_STATUSES.JOINING].join(','),
        clusterId: '',
        reportingScope: EMPLOYEE_REPORTING_SCOPE.ALL,
      });
    },

    // State reset
    resetState: () => {
      set({
        ...initialState,
        // Optionally preserve UI state here
      });
    },

    // Infinite loader helper
    shouldLoadMore: (lastVisibleIndex, threshold = 10) => {
      const { employees, pageInfo, isLoadingMore } = get();
      return !isLoadingMore && 
             pageInfo.hasNextPage && 
             lastVisibleIndex >= employees.length - threshold;
    },

    getEmployeeByIndex: (index) => {
      const { employees } = get();
      return employees[index] || null;
    },

    /**
     * Remove an employee from the repository store
     * @param {string} employeeId - The ID of the employee to remove
     * @returns {boolean} Whether the removal was successful
     */
    removeEmployee: (employeeId) => {
      if (!employeeId) return false;

      const { employees, pageInfo } = get();
      const index = employees.findIndex(emp => emp.id === employeeId);
      
      if (index !== -1) {
        const updatedEmployees = employees.filter(emp => emp.id !== employeeId);
        const updatedPageInfo = {
          ...pageInfo,
          totalCount: Math.max(0, pageInfo.totalCount - 1)
        };
        set({ 
          employees: updatedEmployees,
          pageInfo: updatedPageInfo
        });
        return true;
      } else {
        return false;
      }
    },
  };
});

export default useEmployeesStoreRepository;